import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, Phone, Zap, ChevronRight, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import srLogo from '@/assets/sr-logo.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('/#')) return;
    e.preventDefault();
    const hash = href.slice(1); // e.g. "#warum-wir"
    const scrollToHash = () => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };
    if (location.pathname === '/') {
      scrollToHash();
    } else {
      navigate('/');
      // Wait for page to render, then scroll
      setTimeout(scrollToHash, 300);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/leistungen', label: 'Leistungen' },
    { href: '/#warum-wir', label: 'Warum wir' },
    { href: '/#standort', label: 'Standort' },
    { href: '/team', label: 'Team' },
    { href: '/#prozess', label: 'Prozess' },
    { href: '/#galerie', label: 'Galerie' },
    { href: '/#kontakt', label: 'Kontakt' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-white/95 backdrop-blur-md'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24">
          {/* Logo - always displayed in original colors */}
          <a href="/" className="flex items-center">
            <img 
              src={srLogo} 
              alt="SR-Zerspanung Logo" 
              width={480}
              height={252}
              {...{ fetchpriority: 'high' }}
              className="h-20 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className={`font-normal text-lg uppercase tracking-wide transition-colors duration-300 ${
                  isScrolled 
                    ? 'text-primary hover:text-primary/80' 
                    : 'text-primary hover:text-primary/80'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex flex-col items-end gap-0.5">
              <a 
                href="tel:+4994669115130" 
                className="flex items-center gap-2 font-bold transition-colors text-primary hover:text-primary/80"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden xl:inline">09466 / 911 51 30</span>
              </a>
              <a 
                href="mailto:kontakt@sr-zerspanung.de" 
                className="flex items-center gap-2 text-sm font-medium transition-colors text-primary hover:text-primary/80"
              >
                <Mail className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">kontakt@sr-zerspanung.de</span>
              </a>
            </div>
            <Button variant="fastlane" size="lg" className="gap-2" asChild>
              <a href="/anfrage?type=fastlane">
                <Zap className="w-4 h-4" />
                FastLane Kontakt
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Menü öffnen"
            className={`lg:hidden p-2 transition-colors ${
              isScrolled ? 'text-primary' : 'text-primary'
            }`}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Modern Mobile Menu - Slide-In Sheet */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="right" className="w-[85vw] sm:w-[350px] p-0 flex flex-col bg-primary border-l-0">
          <SheetHeader className="p-6 border-b border-white/20">
            <SheetTitle className="flex items-center">
              <div className="bg-white rounded-lg p-2">
                <img 
                  src={srLogo} 
                  alt="SR-Zerspanung Logo" 
                  width={480}
                  height={252}
                  className="h-10 w-auto"
                />
              </div>
            </SheetTitle>
          </SheetHeader>
          
          {/* Navigation Links with Slide-Up & Blur Animation */}
          <nav className="flex-1 py-6">
            {navLinks.map((link, index) => (
              <motion.a
                key={link.href}
                href={link.href}
                onClick={(e) => { handleAnchorClick(e, link.href); setIsMobileMenuOpen(false); }}
                initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ 
                  delay: 0.1 + index * 0.1, 
                  duration: 0.5,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                className="flex items-center justify-between px-6 py-4 text-lg font-medium text-white hover:bg-white/10 transition-colors group"
              >
                <span className="uppercase tracking-wide">{link.label}</span>
                <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </motion.a>
            ))}
          </nav>
          
          {/* Footer with CTA */}
          <div className="border-t border-white/20 p-6 space-y-4 bg-black/20">
            <motion.a 
              href="tel:+4994669115130" 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.4, type: "spring" }}
              className="flex items-center gap-3 text-white font-semibold text-lg hover:text-white/80 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              09466 / 911 51 30
            </motion.a>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.4, type: "spring" }}
            >
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full gap-2 border-2 border-white bg-white text-primary hover:bg-white/90 hover:text-primary font-bold" 
                asChild
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <a href="/anfrage?type=fastlane">
                  <Zap className="w-5 h-5" />
                  FastLane Kontakt
                </a>
              </Button>
            </motion.div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
